import _ from 'lodash';
import { CheckoutFormValues } from '../checkoutFormValues';
import { Restaurant } from 'models/digitalMap';
import { calculateTotalPrice } from '../calculateTotalPrice';
import { currency } from 'lib/currency';

export interface OrderSummaryItem {
  name: string;
  unitPrice: string;
  image: string;
  quantity: number;
  key: string;
  optionGroups: {
    key: string;
    options: {
      key: string;
      name: string;
      price: string;
    }[];
  }[];
}

export const orderSummaryItemsEqual = (item1: OrderSummaryItem, item2: OrderSummaryItem) => {
  if (item1.key !== item2.key) {
    return false;
  }

  const sortedItem1OptionGroups = _.orderBy(item1.optionGroups, 'key');
  const sortedItem2OptionGroups = _.orderBy(item2.optionGroups, 'key');

  if (sortedItem1OptionGroups.length !== sortedItem2OptionGroups.length) {
    return false;
  }

  return sortedItem1OptionGroups.every((group, index) => {
    const sortedItem2OptionGroup = sortedItem2OptionGroups[index];
    return (
      group.key === sortedItem2OptionGroup.key &&
      _.isEqual(_.orderBy(group.options, 'key'), _.orderBy(sortedItem2OptionGroup.options, 'key'))
    );
  });
};

export const getOrderSummaryItems = (values: CheckoutFormValues, restaurant: Restaurant | null) => {
  if (!restaurant) {
    return [];
  }

  return values.items
    .map(
      (item): OrderSummaryItem => {
        const menuItem = restaurant.menu?.menu_items.find(
          (menuItem) => menuItem.key === item.menuItemKey
        );
        if (!menuItem) {
          throw new Error('MenuItem not found');
        }

        const unitPrice = calculateTotalPrice(menuItem, item, 1);
        return {
          name: menuItem?.name ?? '',
          unitPrice,
          image: menuItem?.media_items.find((mediaItem) => mediaItem.type === 'IMAGE')?.url ?? '',
          quantity: 1,
          key: menuItem?.key ?? '',
          optionGroups: item.optionGroups.map((group) => {
            const menuItemOptionGroup = menuItem.option_groups.find(
              (optionGroup) => optionGroup.key === group.key
            );

            return {
              key: group.key,
              options: group.optionKeys.map((optionKey) => {
                const menuItemOption = menuItemOptionGroup?.options.find(
                  (option) => option.key === optionKey
                );
                return {
                  key: optionKey,
                  name: menuItemOption?.name ?? '',
                  price: menuItemOption?.price ?? '',
                };
              }),
            };
          }),
        };
      }
    )
    .reduce<OrderSummaryItem[]>((acc, item) => {
      // Find an existing item with the same key, add-ons, and option
      const existingItem = acc.find((accItem) => orderSummaryItemsEqual(accItem, item));

      if (existingItem) {
        existingItem.quantity++;
      } else {
        acc.push(item);
      }

      return acc;
    }, []);
};

export const getSubtotal = (orderItems: OrderSummaryItem[], defaultCurrency: string) => {
  return orderItems
    .map((item) => {
      return currency(item.unitPrice).multiply(item.quantity).format();
    })
    .reduce((total, price) => {
      return currency(total).add(currency(price)).format();
    }, `${defaultCurrency}0`);
};
